import {
  type IDefinitionService,
  type IGetCouponSettingsUseCase,
  type ISessionStoreRepository,
  NotInitializedError,
} from '../../../domain/entry.js';

/**
 * Use case for getting coupon settings.
 *
 * The use case first attempts to retrieve user-specific coupon settings from the repository. If no settings are found, it falls back to loading the default coupon settings defined in the product definition.
 * This ensures that there are always valid coupon settings available, even if the user has not customized them.
 *
 * @param couponSettingsRepository Coupon settings repository dependency
 * @param definitionService Definition service to load definition entities
 * @returns Use case executor for getting coupon settings
 */
export function getCouponSettingsUseCase(
  couponSettingsRepository: ISessionStoreRepository,
  definitionService: IDefinitionService
): IGetCouponSettingsUseCase {
  return {
    execute: async ({ product, settingsKey }): ReturnType<IGetCouponSettingsUseCase['execute']> => {
      const settings = await couponSettingsRepository.getCouponSettingsAsync(product, settingsKey);

      if (settings) {
        return settings;
      }

      const definition = await definitionService.loadDefinitionAsync(product);
      if (!definition) {
        throw new NotInitializedError(`Unable to load definition for product: ${product}`);
      }

      // No user settings persisted in repository
      return settingsKey === 'PLAY'
        ? definition.defaultCouponSettingsValues
        : { ...definition.defaultCouponSettingsValues, tipsinfo: [] }; // Return default settings with empty tipsinfo for non-PLAY settings
    },
  };
}
